import React, { useEffect, useMemo, useState } from "react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import { Input } from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import Switch from "@/components/ui/Switch";
import CustomTooltip from "@/components/shared/CustomTooltip";
import { FilterMultiSelect, FilterPill } from "@/components/shared/Filter";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileText,
  Layers,
  Menu,
  Pen,
  Plus,
  FolderPlus,
  FilePlus,
  Info,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";

import { ComplianceService } from "@/services/complianceService";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import useModuleAccess from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { cn } from "@/lib/utils";
import { getCountryList } from "@/utils/countryList";
import { REQUIRED_FIELD_TYPES } from "../../constant";
import {
  buildChecklistBuilderSchema,
  sectionsListSchema,
  subsectionsListSchema,
  requiredFieldsListSchema,
  zodErrorMessages,
  CHECKLIST_NAME_MAX_LENGTH,
  SECTION_NAME_MAX_LENGTH,
  DOCUMENT_NAME_MAX_LENGTH,
  FIELD_NAME_MAX_LENGTH,
} from "../../validation.schema";

// Built once at module scope: the list is ~250 static entries and never
// changes, so rebuilding it per render (or per keystroke) is pure waste.
// A real, stored subsection id (a UUID) as opposed to one this builder minted
// locally. Only stored ids are sent to the server, where they identify the slot
// across a rename.
const isServerId = (id) => typeof id === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id);

const COUNTRY_OPTIONS = getCountryList();

// Imported, not restated: this file used to declare 50 of its own while the
// server accepted 255, so a name the builder silently truncated was a name the
// server would have taken. One number now, in validation.schema.js beside the
// rest of the tree's bounds.

/**
 * "12/100" under a name input.
 *
 * Every name in the tree has a ceiling and every input enforces it with
 * `maxLength`, which stops typing dead with no explanation. The count is what
 * turns that into something the officer can see coming. One component rather
 * than four copies of the markup, because the checklist name, the section, the
 * document and the field all want the identical thing.
 *
 * Goes amber inside the last ten characters: the ceiling only matters when it
 * is close, and colouring it the whole time trains the eye to ignore it.
 */
const CharCount = ({ value, max, className }) => {
  const used = (value ?? "").length;
  return (
    <span className={cn("text-[11px] tabular-nums", used > max - 10 ? "text-tone-amber" : "text-text-secondary", className)}>
      {used}/{max}
    </span>
  );
};

const emptyField = () => ({ fieldName: "", fieldType: "text", isRequired: true });
// Every compliance document is mandatory, so there is no per-document "required"
// toggle; the category key is derived server-side from the name, so the builder
// no longer collects it either.
// Seeded with one blank field so a freshly added document is immediately
// ready to edit instead of showing an empty "no fields" state.
const emptySubsection = () => ({
  id: `new-sub-${Math.random().toString(36).slice(2)}`,
  name: "",
  weightPercentage: 0,
  isRequired: true,
  requiredFields: [emptyField()],
});
// Seeded with one blank document (which itself seeds one blank field) for
// the same reason — a freshly added section is ready to edit right away.
const emptySection = () => ({
  id: `new-sec-${Math.random().toString(36).slice(2)}`,
  name: "",
  subsections: [emptySubsection()],
});

// What counts as a DOCUMENT change, for deciding whether the re-apply wizard
// has anything to say. Deliberately narrow: name, region and the document tree
// — the three things `previewChecklistReapply` diffs. The jobs a checklist
// defaults for are not in here, which is the whole point.
//
// `isRequired` is read with the same `?? true` default the loader applies, so
// a checklist loaded and saved untouched fingerprints identically rather than
// reading as an edit.
const treeFingerprint = (name, region, sections) =>
  JSON.stringify({
    name: (name || "").trim(),
    region: (region || "").trim(),
    sections: (sections || []).map((sec) => ({
      name: (sec.name || "").trim(),
      subsections: (sec.subsections || []).map((sub) => ({
        name: (sub.name || "").trim(),
        weightPercentage: Number(sub.weightPercentage) || 0,
        isRequired: sub.isRequired ?? true,
        requiredFields: (sub.requiredFields || []).map((f) => ({
          fieldName: (f.fieldName || "").trim(),
          fieldType: f.fieldType || "text",
          isRequired: f.isRequired ?? true,
        })),
      })),
    })),
  });

/**
 * A country master's tree, re-keyed as fresh builder rows (ATH-I1765).
 *
 * The ids MUST be new. A master's own section/document ids belong to the master
 * row, and sending them back on a create would point the new checklist's tree at
 * another checklist's rows — `_write_tree` upserts by name, but the id is what
 * the Phase 5D diff reads to recognise a rename, so a borrowed id is a lie about
 * where the row came from.
 *
 * Weights come across as seeded, deliberately below 100 (§B1): the officer adds
 * the one or two documents that define their domain and then distributes to 100,
 * rather than having to reduce something first.
 */
const treeFromMaster = (masterSections = []) =>
  masterSections.map((sec) => ({
    id: `new-sec-${Math.random().toString(36).slice(2)}`,
    name: sec.name ?? "",
    subsections: (sec.subsections ?? []).map((sub) => ({
      id: `new-sub-${Math.random().toString(36).slice(2)}`,
      name: sub.name ?? "",
      weightPercentage: sub.weightPercentage ?? 0,
      isRequired: sub.isRequired ?? true,
      requiredFields: (sub.requiredFields ?? []).map((f) => ({
        fieldName: f.fieldName ?? "",
        fieldType: f.fieldType ?? "text",
        isRequired: f.isRequired ?? true,
      })),
    })),
  }));

/**
 * 3-level checklist builder — sections →
 * subsections (documents) → required fields. Subsection weights must sum to
 * exactly 100. Saves the whole tree via create/update.
 *
 * Laid out as a split pane: the left rail is the section → document tree
 * (navigation), the right pane configures whichever document is selected. A
 * vertical accordion made the deeper levels hard to scan once a checklist had
 * more than a couple of sections.
 *
 * `readOnly` turns the same modal into the card's View Details screen, so the
 * detail view and the editor can never drift apart.
 */
const ChecklistBuilderModal = ({
  open,
  onClose,
  checklistId,
  readOnly = false,
  onSaved,
  // Phase 5D/5F. An edit does not save from here -- it hands the draft up to
  // the page, which closes this builder and opens the re-apply wizard as a
  // FOLLOW-UP. Two dialogs stacked on top of each other is what React error
  // #299 was: the wizard was mounted inside this Radix DialogContent, so it
  // could neither escape the stacking context nor be portalled out of it
  // safely. A sequence of two dialogs is simpler and never overlaps.
  onRequestReapply,
  // Set when the officer came Back from the re-apply wizard. The builder then
  // restores THEIR unsaved edits instead of re-fetching the stored checklist,
  // which is what keeps Back lossless now that the two dialogs are siblings
  // rather than stacked.
  restoreDraft,
  // A country handed in by "Use this template" on a country-set card. The
  // builder preselects it, which runs the same prefill a manual country
  // choice does — one path, not a second one to keep correct.
  prefillRegion = "",
}) => {
  const isEdit = !!checklistId && !readOnly;

  // The version this builder was opened on, handed to the wizard as the
  // lost-update guard.
  const [loadedVersion, setLoadedVersion] = useState(null);
  // The checklist exactly as it was loaded, so the save can tell a document
  // edit from a job-defaults-only edit. The re-apply wizard exists to warn
  // that documents are about to be reset and to choose whose; changing which
  // jobs default to this checklist resets nothing, so it must not open that
  // dialog. The server agrees (`isNoop` on the preview) but only after a round
  // trip, which is the spinner the officer was being shown before it closed
  // itself again.
  const [loadedBaseline, setLoadedBaseline] = useState(null);

  const [name, setName] = useState("");
  const [region, setRegion] = useState("");
  // Which jobs this checklist is the DEFAULT for (Phase D). A default, not an
  // assignment: it only pre-fills the send, and whoever sends a candidate can
  // still choose something else for someone whose requirements differ.
  const [jobIds, setJobIds] = useState([]);
  const [jobOptions, setJobOptions] = useState([]);
  const [jobsOpen, setJobsOpen] = useState(false);
  const [jobsError, setJobsError] = useState(false);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Country document sets (ATH-I1765) ─────────────────────────────────
  // Picking a country offers that country's predefined documents as a
  // starting point, so the common set is never typed twice.
  //
  // The source is the tenant's OWN master row, not the shipped registry: a
  // tenant that amended its India set gets their version, which is the whole
  // reason the master is a per-tenant row.
  const [masters, setMasters] = useState([]);
  const [prefilling, setPrefilling] = useState(false);
  // Which country the current tree was prefilled from, so the notice can
  // explain the sub-100 total the officer is looking at.
  const [prefilledFrom, setPrefilledFrom] = useState(null);
  // Whether the checklist being EDITED is a country document set. It is
  // judged by a different weight rule — 1..100 rather than exactly 100 —
  // because a set is a source a checklist is built from and the headroom is
  // the point. The server already enforces this; the builder has to agree, or
  // it refuses to save what the server would accept.
  const [editingMaster, setEditingMaster] = useState(false);

  // Accordion collapse state per section id
  const [collapsedSections, setCollapsedSections] = useState({});
  const toggleSectionCollapse = (secId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [secId]: !prev[secId],
    }));
  };
  // `AccordionPrimitive.Root` is controlled by the ids that are open, which
  // is the inverse of `collapsedSections` -- every section defaults to open
  // (matching the old `!!collapsedSections[id]` default of false).
  const openSectionIds = sections.map((sec) => sec.id).filter((id) => !collapsedSections[id]);
  // The root reports the *whole* new open list, not which item changed --
  // find that one id and hand it to the same toggle used everywhere else,
  // so this is still a single flip rather than a second source of truth.
  const handleAccordionValueChange = (newOpenIds) => {
    const changedId = sections.map((sec) => sec.id).find((id) => newOpenIds.includes(id) !== openSectionIds.includes(id));
    if (changedId != null) toggleSectionCollapse(changedId);
  };

  // Which document the right pane is editing.
  const [selectedSubId, setSelectedSubId] = useState(null);
  // Section whose name is being renamed (a pencil unlocks it; the name is not
  // a permanently open input, which read as "unsaved" at rest).
  const [editingSectionId, setEditingSectionId] = useState(null);

  // Errors are only shown once the user clicks Create/Save Checklist.
  const [showErrors, setShowErrors] = useState(false);
  // The name the server refused as a duplicate. Held here rather than routed
  // through `serverFieldErrors`, which reads the `errors[]` array a 422
  // carries; a domain AppError sends a flat body, so it would find nothing.
  // Cleared as soon as the officer edits the name, so the error never outlives
  // the value it was about.
  const [nameTaken, setNameTaken] = useState(null);

  // Checklist usage: in-flight candidates + documents already attached. Null
  // until loaded (or when not editing) — the banner is suppressed rather than
  // rendered empty.
  const [usage, setUsage] = useState(null);

  // Load form state whenever the modal opens.
  // The country document sets available to start from. Loaded on CREATE only:
  // an edit already has its tree, and offering to replace it is the re-apply
  // wizard's job, not this one's.
  useEffect(() => {
    if (!open || checklistId || readOnly) {
      setMasters([]);
      return undefined;
    }
    let active = true;
    ComplianceService.getChecklistMasters()
      .then((res) => {
        if (active) setMasters(res?.masters ?? []);
      })
      .catch((e) => {
        // A failed load costs the officer the shortcut, not the screen —
        // they can still build the checklist by hand, so this stays
        // silent rather than throwing a toast at them for a convenience
        // they had not asked for yet.
        console.error(e);
        if (active) setMasters([]);
      });
    return () => {
      active = false;
    };
  }, [open, checklistId, readOnly]);

  useEffect(() => {
    if (!open) return undefined;

    setSubmitting(false);
    setShowErrors(false);
    setEditingSectionId(null);
    setCollapsedSections({});
    // The modal is never unmounted — `open` only toggles — so every piece of
    // transient UI state outlives a close. Left alone, the job dropdown that
    // was open when the officer saved an edit was still open when the New
    // Checklist modal was opened next.
    setJobsOpen(false);
    setPrefilledFrom(null);
    setEditingMaster(false);

    if (!checklistId) {
      setName("");
      setRegion(prefillRegion || "");
      setSections([]);
      setSelectedSubId(null);
      setLoadedBaseline(null);
      return undefined;
    }

    // Coming Back from the wizard: reinstate the edits the officer had,
    // not the version on the server. Re-fetching here would silently
    // discard their work the moment they pressed Back.
    if (restoreDraft) {
      setName(restoreDraft.name ?? "");
      setRegion(restoreDraft.region ?? "");
      const tree = (restoreDraft.sections ?? []).map((sec, si) => ({
        id: sec.id ?? `restored-sec-${si}`,
        name: sec.name ?? "",
        subsections: (sec.subsections ?? []).map((sub, ssi) => ({
          id: sub.id ?? `restored-sub-${si}-${ssi}`,
          name: sub.name ?? "",
          weightPercentage: sub.weightPercentage ?? 0,
          requiredFields: (sub.requiredFields ?? []).map((f) => ({
            fieldName: f.fieldName ?? "",
            fieldType: f.fieldType ?? "text",
            isRequired: f.isRequired ?? true,
          })),
        })),
      }));
      setSections(tree);
      setSelectedSubId(tree.flatMap((x) => x.subsections)[0]?.id ?? null);
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    ComplianceService.getChecklist(checklistId)
      .then((res) => {
        if (!active) return;
        const cl = res?.checklist ?? {};
        setName(cl.name ?? "");
        setRegion(cl.region ?? "");
        // The version this editor was OPENED on. Sent back on save so
        // the server can refuse a lost update (Phase 5D): a whole-tree
        // write built from a stale view would silently resurrect
        // whatever another officer had just removed.
        setLoadedVersion(cl.version ?? null);
        // A country document set is judged by the master weight rule.
        setEditingMaster(!!cl.isCountryMaster);
        const tree = (cl.sections ?? []).map((sec) => ({
          id: sec.id,
          name: sec.name ?? "",
          subsections: (sec.subsections ?? []).map((sub) => ({
            id: sub.id,
            name: sub.name ?? "",
            weightPercentage: sub.weightPercentage ?? 0,
            isRequired: sub.isRequired ?? true,
            requiredFields: (sub.requiredFields ?? []).map((f) => ({
              fieldName: f.fieldName ?? "",
              fieldType: f.fieldType ?? "text",
              isRequired: f.isRequired ?? true,
            })),
          })),
        }));
        setSections(tree);
        // Snapshot what was loaded, for the document-change test at save.
        setLoadedBaseline(treeFingerprint(cl.name ?? "", cl.region ?? "", tree));
        // Open on the first document so the right pane is never blank
        // on a checklist that has one.
        setSelectedSubId(tree.flatMap((s) => s.subsections)[0]?.id ?? null);
      })
      .catch((e) => {
        console.error(e);
        toast.error(apiErrorMessageFormat(e, "Failed to load checklist"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, checklistId, restoreDraft]);

  // Who is mid-process against this checklist. Removing a document no longer
  // destroys anything — since Phase 5E the tree writer soft-deletes, so an
  // upload already made against a removed slot keeps its link and comes back
  // if the requirement is re-added. The warning still matters, though: the
  // requirement vanishes from the candidate's portal and stops counting
  // toward their weighted progress the moment it is saved. Fetched only when
  // actually editing: a brand-new checklist has no usage, and a read-only view
  // changes nothing.
  useEffect(() => {
    if (!open || !isEdit || !checklistId) {
      setUsage(null);
      return undefined;
    }
    let active = true;
    ComplianceService.getChecklistUsage(checklistId)
      .then((res) => {
        if (active) setUsage(res ?? null);
      })
      .catch(() => {
        // Non-fatal: the warning is an aid, not a gate. Failing to load
        // it must never block an edit the user is entitled to make.
        if (active) setUsage(null);
      });
    return () => {
      active = false;
    };
  }, [open, isEdit, checklistId]);
  // The jobs available to pick from. Loaded once per open rather than per
  // keystroke: the list is small enough to filter in the dropdown, and a
  // search-as-you-type against the admin job list would fire on every
  // character while the officer is really just ticking boxes.
  useEffect(() => {
    if (!open) return;
    let active = true;
    setJobsError(false);
    // Deliberately the SAME shape the admin job list page sends, which is known
    // to work: paginated, with an empty search. `pagination: false` is a
    // supported parameter but a differently-exercised branch, and there is no
    // reason for a picker of at most a page of jobs to be the only caller
    // taking it.
    ComplianceService.getChecklistJobOptions(checklistId)
      .then((res) => {
        if (!active) return;
        const jobs = res?.jobs ?? [];
        if (!jobs.length) {
          // A 200 with no rows is a different problem from a failed request,
          // and the two used to look identical in the dropdown. Say which.
          console.warn("[checklist builder] job list returned no rows", res);
        }
        setJobOptions(
          jobs.map((j) => ({
            value: j.jobId,
            label: j.title || "Untitled job",
            // A job another checklist already defaults for is shown greyed and
            // named, not hidden: `UNIQUE (job_id)` means the save would be
            // refused, and a row that silently vanishes cannot explain itself.
            disabled: Boolean(j.takenByChecklistId),
            hint: j.takenByChecklistName ? `Default for ${j.takenByChecklistName}` : undefined,
          })),
        );
      })
      .catch((e) => {
        // Non-fatal — the rest of the builder works and the tree still saves —
        // but NOT silent. Swallowing this into an empty array made a refused
        // request read as "this tenant has no jobs", which is a different and
        // much more confusing claim.
        console.error(e);
        if (!active) return;
        setJobOptions([]);
        setJobsError(true);
      });
    return () => {
      active = false;
    };
    // Keyed on the checklist too: which jobs count as "taken" is relative to
    // the checklist being edited, so the create case and each edit get their
    // own answer rather than sharing whichever loaded first.
  }, [open, checklistId]);

  // Which of them this checklist already defaults for.
  useEffect(() => {
    if (!open || !checklistId) {
      setJobIds([]);
      return undefined;
    }
    let active = true;
    ComplianceService.getChecklistJobs(checklistId)
      .then((res) => {
        if (active) setJobIds((res?.jobs ?? []).map((j) => j.jobId));
      })
      .catch((e) => {
        console.error(e);
        if (active) setJobIds([]);
      });
    return () => {
      active = false;
    };
  }, [open, checklistId]);

  // The chip's text. `jobIds` holds ids and the titles live in `jobOptions`,
  // which is a separate read — so a stored link can arrive before, or without,
  // its option (a job outside the list, or the options request having failed).
  // Falling through to the id would put a raw UUID on screen, so the chip says
  // what it actually knows instead.
  const jobLabelFor = (id) => jobOptions.find((o) => o.value === id)?.label || "Loading…";

  // ── weight total ──────────────────────────────────────────────────────────
  const { weightTotal, subCount } = useMemo(() => {
    let total = 0;
    let count = 0;
    sections.forEach((sec) =>
      sec.subsections.forEach((sub) => {
        total += Number(sub.weightPercentage) || 0;
        count += 1;
      }),
    );
    return { weightTotal: total, subCount: count };
  }, [sections]);

  // The two weight rules (§2.3), and the builder has to apply the same one
  // the server will. A country document set may total 1..100 — it is a source
  // a checklist is built from, and the leftover room is what the officer
  // spends on the documents that define their domain. An assignable checklist
  // must total exactly 100, because that total is the denominator every
  // candidate's progress is measured against.
  //
  // Getting this wrong is not cosmetic: with the regular rule applied to a
  // master, Save is refused on a total the server would happily accept, and
  // the officer cannot edit a country set at all.
  // The shape check in the same mode as `weightsValid` below. Both gates read
  // one rule: the zod schema ALSO enforced a hard `=== 100`, so relaxing only
  // the footer check still left the save refused — which was the reported bug.
  const builderSchema = useMemo(() => buildChecklistBuilderSchema({ isCountryMaster: editingMaster }), [editingMaster]);

  const weightsValid = editingMaster ? weightTotal >= 1 && weightTotal <= 100 : weightTotal === 100;

  // ── Country prefill (ATH-I1765) ───────────────────────────────────────

  const applyPrefill = async (master) => {
    setPrefilling(true);
    try {
      const res = await ComplianceService.getChecklist(master.id);
      const tree = treeFromMaster(res?.checklist?.sections ?? []);
      setSections(tree);
      setSelectedSubId(tree.flatMap((sec) => sec.subsections)[0]?.id ?? null);
      setPrefilledFrom(master.region || master.name || null);
    } catch (e) {
      console.error(e);
      toast.error("Could not load that country's documents. Add them manually.");
    } finally {
      setPrefilling(false);
    }
  };

  // Country select. Sets the region and nothing else.
  //
  // ⚠️ **Picking a country deliberately does NOT prefill**, even when that
  // country has a document set. "New Checklist" is the manual path: the
  // officer chose to build it themselves, and quietly filling eleven
  // documents in underneath them is the tool overruling that choice — they
  // then have to delete what they never asked for.
  //
  // Starting from a country set is its own, explicit action: "Use this
  // template" on that set's card, which opens this builder with
  // `prefillRegion` already set. Two entry points, one of which the officer
  // asked for.
  const handleRegionChange = (nextRegion) => {
    setRegion(nextRegion);
  };

  // "Use this template" hands in a country rather than clicking the Select,
  // so the prefill has to be run here too — `handleRegionChange` never fires.
  // Guarded on an empty tree and a not-yet-prefilled state so it runs exactly
  // once per open, and only for the create path.
  useEffect(() => {
    if (!open || checklistId || readOnly) return;
    if (!prefillRegion || prefilledFrom || prefilling) return;
    if (!masters.length || sections.length) return;
    const master = masters.find((m) => (m.region ?? "").trim().toLowerCase() === prefillRegion.trim().toLowerCase());
    if (master) applyPrefill(master);
    // `masters` is the trigger: it arrives async, after the region is set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, checklistId, readOnly, prefillRegion, masters, prefilledFrom, prefilling, sections.length]);

  // A killed Compliance leaves this modal open as a reader — the checklist
  // still loads and every section is still visible — and refuses the save,
  // which the server would answer 503 anyway.
  const { isAvailable } = useModuleAccess(MODULES.COMPLIANCE);

  // ── live validation ───────────────────────────────────────────────────────
  // Re-run on every keystroke: it drives the disabled Save button, the
  // "what is still missing" tooltip, and the inline field highlights.
  const validation = useMemo(() => {
    const result = builderSchema.safeParse({ name, region, sections });
    return result.success ? { ok: true, messages: [] } : { ok: false, messages: zodErrorMessages(result.error) };
  }, [builderSchema, name, region, sections]);

  // The first message each name input has to show, from the schema that also
  // gates Save — so "must contain letters" and "at least 2 characters" land
  // under the input that broke them instead of only in the Save tooltip.
  // Keyed by path: "name", "0.name" for a section, "0.1.name" for a document,
  // "0.1.2.fieldName" for a field.
  const nameIssues = useMemo(() => {
    const result = {};
    const record = (key, message) => {
      if (key && !(key in result)) result[key] = message;
    };

    const parsed = builderSchema.safeParse({ name, region, sections });
    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const [head, ...rest] = issue.path;
        if (head === "name" && rest.length === 0) record("name", issue.message);
        if (head !== "sections") return;
        // sections[si] -> .subsections[ssi] -> .requiredFields[fi]
        const [si, k1, ssi, k2, fi] = rest;
        if (k1 === undefined) return;
        if (k1 === "name") record(`${si}.name`, issue.message);
        if (k1 === "subsections" && k2 === "name") record(`${si}.${ssi}.name`, issue.message);
        if (k1 === "subsections" && k2 === "requiredFields") {
          const leaf = rest[rest.length - 1];
          if (leaf === "fieldName") record(`${si}.${ssi}.${fi}.fieldName`, issue.message);
        }
      });
    }
    return result;
  }, [builderSchema, name, region, sections]);

  const missingFieldsTooltip = useMemo(() => {
    if (validation.ok && weightsValid) return null;
    const items = [];
    if (!validation.ok && validation.messages?.length > 0) {
      items.push(...validation.messages);
    }
    if (!weightsValid) {
      items.push(editingMaster ? `Total weight must be between 1% and 100% (currently ${weightTotal}%)` : `Total weight must equal 100% (currently ${weightTotal}%)`);
    }
    if (items.length === 0) return null;

    return (
      <div className="flex flex-col gap-1 text-left py-0.5 max-w-[240px]">
        <div className="font-semibold text-text-primary text-[11.5px]">Missing fields:</div>
        <ul className="list-disc pl-3.5 space-y-0.5 text-text-secondary text-[11px]">
          {items.map((msg, idx) => (
            <li key={idx} className="leading-tight">
              {msg}
            </li>
          ))}
        </ul>
      </div>
    );
  }, [validation, weightsValid, weightTotal, editingMaster]);

  // ── mutators (immutable updates keyed by id/index) ────────────────────────
  const updateSection = (si, patch) => setSections((prev) => prev.map((s, i) => (i === si ? { ...s, ...patch } : s)));

  const removeSection = (si) =>
    setSections((prev) => {
      const removed = prev[si];
      if (removed?.subsections.some((sub) => sub.id === selectedSubId)) {
        setSelectedSubId(null);
      }
      return prev.filter((_, i) => i !== si);
    });

  // A new section is only useful once every existing one is already
  // complete — otherwise checklists accumulate empty/unnamed sections that
  // just fail validation later at save time instead of right where the
  // user is.
  const addSection = () => {
    const result = sectionsListSchema.safeParse(sections);
    if (!result.success) {
      setShowErrors(true);
      return;
    }
    // The gate passed, so whatever earlier failed attempt turned errors on
    // is resolved now — reset before the new, still-blank section renders,
    // otherwise it would immediately flash as invalid.
    setShowErrors(false);
    const sec = emptySection();
    setSections((prev) => [...prev, sec]);
    setEditingSectionId(sec.id);
    setSelectedSubId(sec.subsections[0]?.id ?? null);
  };

  const updateSub = (si, ssi, patch) =>
    setSections((prev) =>
      prev.map((s, i) =>
        i === si
          ? {
              ...s,
              subsections: s.subsections.map((ss, j) => (j === ssi ? { ...ss, ...patch } : ss)),
            }
          : s,
      ),
    );

  const removeSub = (si, ssi) =>
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== si) return s;
        const removed = s.subsections[ssi];
        if (removed?.id === selectedSubId) setSelectedSubId(null);
        return { ...s, subsections: s.subsections.filter((_, j) => j !== ssi) };
      }),
    );

  // A new document is only useful once every existing document in this
  // section is already complete (named, with at least one named field).
  const addSub = (si) => {
    const result = subsectionsListSchema.safeParse(sections[si].subsections);
    if (!result.success) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    const sub = emptySubsection();
    setSections((prev) => prev.map((s, i) => (i === si ? { ...s, subsections: [...s.subsections, sub] } : s)));
    setSelectedSubId(sub.id);
  };

  // The right pane always acts on the selected document, so field mutators
  // locate it by id rather than threading indices through the tree.
  const patchSelectedSub = (mutate) =>
    setSections((prev) =>
      prev.map((s) => ({
        ...s,
        subsections: s.subsections.map((ss) => (ss.id === selectedSubId ? mutate(ss) : ss)),
      })),
    );

  const updateFieldOfSelected = (fi, patch) =>
    patchSelectedSub((ss) => ({
      ...ss,
      requiredFields: ss.requiredFields.map((f, k) => (k === fi ? { ...f, ...patch } : f)),
    }));

  const removeFieldFromSelected = (fi) =>
    patchSelectedSub((ss) => ({
      ...ss,
      requiredFields: ss.requiredFields.filter((_, k) => k !== fi),
    }));

  // A new field is only useful once every existing field on this document
  // already has a name.
  const addFieldToSelected = () => {
    if (!selected) return;
    const result = requiredFieldsListSchema.safeParse(selected.sub.requiredFields);
    if (!result.success) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    patchSelectedSub((ss) => ({ ...ss, requiredFields: [...ss.requiredFields, emptyField()] }));
  };

  // The selected document, plus where it lives (needed by the field editors).
  const selected = useMemo(() => {
    for (let si = 0; si < sections.length; si += 1) {
      const ssi = sections[si].subsections.findIndex((ss) => ss.id === selectedSubId);
      if (ssi !== -1) {
        return { si, ssi, section: sections[si], sub: sections[si].subsections[ssi] };
      }
    }
    return null;
  }, [sections, selectedSubId]);

  // The name messages for the document currently open in the right pane, and
  // for each of its fields. Only one document is editable at a time, so these
  // read off `selected` rather than being computed for the whole tree.
  const documentNameError = selected ? nameIssues[`${selected.si}.${selected.ssi}.name`] : undefined;
  const fieldNameErrorAt = (fi) => (selected ? nameIssues[`${selected.si}.${selected.ssi}.${fi}.fieldName`] : undefined);

  // ── submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setShowErrors(true);

    const parsed = builderSchema.safeParse({ name, region, sections });
    if (!parsed.success) {
      toast.error("Please fill the required fields");
      return;
    }
    if (!weightsValid) {
      toast.error(
        editingMaster ? `Document weights must total between 1% and 100% (currently ${weightTotal}%)` : `Document weights must total 100% (currently ${weightTotal}%)`,
      );
      return;
    }
    if (!isAvailable) return;

    const payload = {
      name: name.trim(),
      region: region.trim() || null,
      sections: sections.map((sec, si) => ({
        name: sec.name.trim(),
        displayOrder: si,
        subsections: sec.subsections.map((sub, ssi) => ({
          // The stored subsection id, when this slot came from the
          // server. It is what lets a RENAME be recognised as a
          // rename: matching on name obviously cannot survive a name
          // change, and the category key is derived FROM the name, so
          // it changes too. Locally-added slots have a synthetic id
          // and send none.
          id: isServerId(sub.id) ? sub.id : undefined,
          name: sub.name.trim(),
          weightPercentage: Number(sub.weightPercentage) || 0,
          isRequired: sub.isRequired ?? true,
          displayOrder: ssi,
          requiredFields: sub.requiredFields
            .filter((f) => f.fieldName.trim())
            .map((f, fi) => ({
              fieldName: f.fieldName.trim(),
              fieldType: f.fieldType,
              isRequired: f.isRequired ?? true,
              displayOrder: fi,
            })),
        })),
      })),
    };

    // An EDIT goes through the re-apply wizard: it can move requirements
    // under candidates who are mid-process, so the officer chooses who it
    // applies to and confirms the cost. A CREATE has no candidates yet and
    // nothing to re-apply, so it saves straight through -- putting a
    // consequences dialog in front of it would be friction with no content.
    //
    // A COUNTRY SET is the second case, not the first. Nothing is ever
    // assigned to one (§2.2), so the wizard has no candidates to ask
    // about -- and the server refuses the re-apply routes for a master
    // outright, which is what made "Apply & Re-Review" a dead end the
    // officer could not save through at all. It is the tenant's own copy,
    // so the edit lands straight on it.
    if (isEdit && !editingMaster) {
      // The job links are saved HERE, before the handoff. They are a separate
      // write on purpose: the re-apply wizard exists to warn that documents
      // are about to be reset and to let the officer choose whose, and a job
      // default resets nothing — so routing it through that wizard would ask
      // for a decision this change does not need. It also keeps it plain that
      // a candidate already mid-process is untouched when their job gains a
      // default.
      try {
        await ComplianceService.setChecklistJobs(checklistId, jobIds);
      } catch (e) {
        console.error(e);
        toast.error(apiErrorMessageFormat(e, "Failed to save the job defaults"));
        return;
      }

      // A job-defaults-only edit stops here. The re-apply wizard exists to
      // warn that documents are about to be reset and to let the officer
      // choose whose; job defaults reset nothing, so opening it would ask for
      // a decision this change does not need. The server reaches the same
      // answer (`isNoop`), but only after the preview round trip — which is a
      // modal that opens on a spinner and then closes itself, and reads as a
      // glitch.
      //
      // Narrow on purpose: the ONLY case skipped is a tree that did not
      // change. Any document edit still goes through the wizard, untouched.
      const documentsUnchanged = loadedBaseline !== null && treeFingerprint(name, region, sections) === loadedBaseline;
      if (documentsUnchanged) {
        toast.success("Job defaults saved");
        onSaved?.();
        onClose?.();
        return;
      }

      // Hand off and close. The page reopens this builder with the draft
      // if the officer comes Back, so nothing is lost.
      onRequestReapply?.({
        payload,
        checklistId,
        checklistName: name,
        loadedVersion,
        usage,
      });
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        // Only a country set reaches here as an edit: the wizard took
        // every other one above. It is the tenant's own copy, so the
        // edit is written straight to that row.
        await ComplianceService.updateChecklist(checklistId, payload);
        toast.success("Country documents saved");
      } else {
        const created = await ComplianceService.createChecklist(payload);
        // The link needs the id the create just minted, so it follows rather
        // than riding along. A failure here leaves a valid checklist with no job
        // defaults, which is a recoverable state the officer can fix by editing
        // — so it warns instead of claiming the whole save failed.
        const newId = created?.checklist?.id ?? created?.checklistId ?? created?.id;
        if (jobIds.length && newId) {
          try {
            await ComplianceService.setChecklistJobs(newId, jobIds);
          } catch (e) {
            console.error(e);
            toast.error(apiErrorMessageFormat(e, "Checklist saved, but the job defaults did not. Edit it to try again."));
          }
        }
        toast.success("Checklist created");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      // A name collision belongs on the Name input, not only in a toast that
      // scrolls away from the field the officer has to change.
      if (e?.response?.data?.error_code === "COMPLIANCE_CHECKLIST_NAME_CONFLICT") {
        setNameTaken(name.trim());
        setShowErrors(true);
      }
      toast.error(apiErrorMessageFormat(e, "Failed to save checklist"));
    } finally {
      setSubmitting(false);
    }
  };

  // A country set is a different kind of thing, so the header says so — the
  // officer should know they are changing what every future checklist for
  // that country starts from, not one checklist.
  const title = readOnly
    ? editingMaster
      ? "Country Documents"
      : "Checklist Details"
    : editingMaster
      ? "Edit Country Documents"
      : isEdit
        ? "Edit Checklist"
        : "New Checklist";

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose?.();
      }}
    >
      <DialogContent className="w-[980px] md:w-full max-w-[1040px] flex flex-col p-0 max-h-[92vh]">
        <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] items-start gap-3 pr-8">
            <div>
              <DialogTitle className="text-[15px] font-semibold text-text-primary">{title}</DialogTitle>
              <DialogDescription className="text-xs text-text-primary/70 mt-0.5">
                {readOnly
                  ? "Sections, documents and the fields collected for each."
                  : editingMaster
                    ? "The documents this country's compliance rules require. Checklists built from it start here, so weights may total anything up to 100% — the rest is theirs to assign."
                    : "Define sections, documents and the fields to collect. Document weights must total 100%."}
              </DialogDescription>
            </div>

            {/* <div className="flex items-center gap-3">
                            <span className="text-xs text-text-primary/70 whitespace-nowrap">
                                {subCount} document{subCount === 1 ? "" : "s"}
                            </span>
                        </div> */}
          </div>
          <DialogClose className="cursor-pointer absolute right-5 top-4 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        {loading ? (
          <p className="text-[13px] text-text-primary/60 py-16 text-center">Loading…</p>
        ) : (
          <>
            {/* In-use warning. This used to say the edit silently
                            unlinked uploads, which it did — the tree writer
                            hard-deleted removed subsections against an
                            ON DELETE SET NULL FK. Phase 5E made that a
                            soft-delete, so nothing is destroyed any more and the
                            copy below no longer claims otherwise. What is still
                            worth warning about is the live effect: the document
                            leaves the candidate's portal and stops counting
                            toward their progress as soon as this is saved. */}
            {usage?.editRisk && (
              <div className="mx-5 mt-3 rounded-lg border border-tone-amber/30 bg-tone-amber-soft px-3 py-2 shrink-0">
                <p className="text-[11.5px] font-semibold text-tone-amber">
                  This checklist is in use
                  {usage.inFlightCount > 0 ? ` — ${usage.inFlightCount} candidate${usage.inFlightCount === 1 ? " is" : "s are"} mid-process` : ""}
                  {usage.attachedDocumentCount > 0 ? `, ${usage.attachedDocumentCount} document${usage.attachedDocumentCount === 1 ? "" : "s"} already uploaded` : ""}.
                </p>
                <p className="text-[11px] text-text-primary/70 mt-0.5">
                  Removing a document withdraws it from these candidates and stops it counting toward their progress. Uploads already made against it are kept, and are
                  restored if you add the document back. Adding new documents is safe.
                  {usage.candidates?.length > 0 && (
                    <>
                      {" "}
                      Affected:{" "}
                      {usage.candidates
                        .filter((c) => c.inFlight)
                        .slice(0, 3)
                        .map((c) => c.candidateName)
                        .join(", ")}
                      {usage.inFlightCount > 3 ? ` +${usage.inFlightCount - 3} more` : ""}
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Checklist meta */}
            <div className="flex gap-3 px-5 py-3 border-b border-border-primary shrink-0">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-text-primary/80">Name</label>
                  <CharCount value={name} max={CHECKLIST_NAME_MAX_LENGTH} />
                </div>
                <Input
                  value={name}
                  maxLength={CHECKLIST_NAME_MAX_LENGTH}
                  disabled={readOnly}
                  onChange={(e) => {
                    setName(e.target.value.slice(0, CHECKLIST_NAME_MAX_LENGTH));
                    // The refusal was about the old value; it must not outlive it.
                    setNameTaken(null);
                  }}
                  placeholder="e.g. India Onboarding"
                  className={cn("rounded-lg", ((showErrors && nameIssues.name) || nameTaken) && "border-tone-rose")}
                />
                {nameTaken ? (
                  <p className="mt-1 text-[11px] text-tone-rose">A checklist named &ldquo;{nameTaken}&rdquo; already exists. Please choose a different name.</p>
                ) : (
                  showErrors && nameIssues.name && <p className="mt-1 text-[11px] text-tone-rose">{nameIssues.name}</p>
                )}
              </div>
              <div className="w-56">
                <label className="block text-xs font-medium mb-1 text-text-primary/80">
                  Country
                  <span className="text-tone-rose font-bold text-sm ml-1">*</span>
                </label>
                {/* Same country list the tenant form uses, so a
                                    checklist region and a tenant region are always
                                    spelled identically — the region filter matches
                                    on that string. */}
                <Select
                  value={region}
                  disabled={readOnly || prefilling}
                  onChange={handleRegionChange}
                  options={COUNTRY_OPTIONS}
                  placeholder="Select country"
                  searchable
                  searchPlaceholder="Search country..."
                  className={cn(
                    "h-auto min-h-[38px] rounded-lg border-border-primary bg-background-form/40 px-2 py-1.5 text-xs text-text-primary",
                    showErrors && !region.trim() && "border-tone-rose",
                  )}
                />
                {showErrors && !region.trim() && <p className="mt-1 text-[11px] text-tone-rose">Please select a country</p>}
              </div>
            </div>

            {/* Phase D. Which jobs get this checklist as their DEFAULT. Its own
              row rather than a third column, because the row above is a
              two-column flex whose geometry a third control would change.

              Inline, never a nested dialog: a dialog inside this one's content
              creates a stacking context it can never paint above (React error
              #299 — the re-apply wizard already learned that here). */}
            <div className="px-5 py-3 border-b border-border-primary shrink-0">
              <label className="block text-xs font-medium mb-1 text-text-primary/80">Default for jobs</label>
              <div className="flex items-start gap-3">
                <div className="w-full sm:w-80">
                  <FilterMultiSelect
                    label="Select jobs"
                    value={jobIds}
                    onChange={setJobIds}
                    options={jobOptions}
                    open={jobsOpen}
                    onToggle={() => setJobsOpen((v) => !v)}
                    disabled={readOnly}
                    emptyText={jobsError ? "Could not load the job list. Close and reopen to retry." : "No jobs to choose from yet."}
                    fullWidth
                  />
                </div>
                <p className="flex-1 text-[11px] leading-snug text-text-secondary">
                  Candidates on these jobs are sent to compliance with this checklist already filled in. It is a starting point — whoever sends them can still pick a
                  different checklist for a candidate whose requirements differ, and anyone already mid-process keeps the checklist they were sent.
                </p>
              </div>

              {/* The picked jobs, each removable. The trigger above says only
                "N selected", which names a count rather than the jobs — and
                un-picking one meant reopening the dropdown and hunting for it.
                `FilterPill` is the chip that belongs here: same file as the
                control it sits under, same accent tokens.

                Removal is withheld in `readOnly` for the same reason the
                trigger is: the View Details screen shows what is selected
                without offering to change it. */}
              {jobIds.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {jobIds.map((id) => (
                    <FilterPill key={id} label={jobLabelFor(id)} onRemove={readOnly ? undefined : () => setJobIds((prev) => prev.filter((x) => x !== id))} />
                  ))}
                </div>
              )}
            </div>

            {/* Split pane: tree (left) / document editor (right) */}
            <div className="flex flex-col md:flex-row flex-1 min-h-0">
              {/* LEFT — section → document tree */}
              <div className="w-full md:w-[35%] border-b md:border-b-0 md:border-r border-border-primary overflow-y-auto custom-scrollbar p-3 flex flex-col gap-2.5">
                {/* Top bar: N Documents + Add Section button */}
                <div className="flex items-center justify-between pb-1">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-text-primary">
                    <Menu size={14} className="text-text-primary/70" />
                    <span>
                      {subCount} {subCount === 1 ? "Document" : "Documents"}
                    </span>
                  </div>
                  {!readOnly && (
                    <ButtonShadcn
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={addSection}
                      className="h-7 px-2.5 rounded-lg text-xs font-medium border-border-primary hover:bg-background-tertiary flex items-center gap-1"
                    >
                      <FolderPlus size={10} /> Section
                    </ButtonShadcn>
                  )}
                </div>

                {sections.length === 0 ? (
                  <div className="flex flex-col gap-3">
                    <div className="rounded-xl border border-border-primary p-6 flex flex-col items-center justify-center text-center gap-2 bg-background-form/10">
                      <p className="text-xs font-bold text-text-primary">No sections yet</p>
                      <p className="text-[11.5px] text-text-secondary">Add a section to define required documents</p>
                      {!readOnly && (
                        <ButtonShadcn
                          type="button"
                          onClick={addSection}
                          className="mt-2 bg-button-secondary text-text-base text-xs rounded-lg px-3 py-1.5 flex items-center gap-1.5"
                        >
                          <FolderPlus size={13} /> Add Section
                        </ButtonShadcn>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <AccordionPrimitive.Root multiple value={openSectionIds} onValueChange={handleAccordionValueChange} className="flex flex-col gap-2.5">
                      {sections.map((sec, si) => {
                        const sectionEmpty = sec.subsections.length === 0;
                        const sectionNameError = nameIssues[`${si}.name`];
                        const flagSection = showErrors && (sectionEmpty || !!sectionNameError);
                        const isCollapsed = !!collapsedSections[sec.id];

                        return (
                          <AccordionPrimitive.Item
                            key={sec.id}
                            value={sec.id}
                            className={cn("rounded-xl border p-2.5 bg-background-form/20 transition-all", flagSection ? "border-tone-rose/60" : "border-border-primary")}
                          >
                            {/* Section header */}
                            <div className="flex items-center gap-2">
                              {/* Accordion Chevron */}
                              <AccordionPrimitive.Trigger
                                title={isCollapsed ? "Expand section" : "Collapse section"}
                                className="h-6 w-6 shrink-0 rounded-lg bg-button-primary/10 font-mono text-[11px] font-bold text-button-primary flex items-center justify-center transition-colors hover:bg-button-primary/20 cursor-pointer"
                              >
                                {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                              </AccordionPrimitive.Trigger>

                              <div className="flex-1">
                                <Input
                                  value={sec.name}
                                  disabled={readOnly}
                                  onChange={(e) =>
                                    updateSection(si, {
                                      name: e.target.value,
                                    })
                                  }
                                  placeholder="e.g. Government Documents"
                                  maxLength={SECTION_NAME_MAX_LENGTH}
                                  className={cn(
                                    "w-full rounded-xl py-1.5 px-3 text-xs font-semibold bg-background-secondary border-border-primary",
                                    showErrors && sectionNameError && "border-tone-rose",
                                  )}
                                />
                                {/* The count sits beside the error rather than
                                    above the input: this header row has no
                                    label to hang it from. */}
                                <div className="mt-1 flex items-start justify-between gap-2">
                                  <p className="text-[11px] text-tone-rose">{(showErrors && sectionNameError) || ""}</p>
                                  <CharCount value={sec.name} max={SECTION_NAME_MAX_LENGTH} className="shrink-0" />
                                </div>
                              </div>

                              {!readOnly && (
                                <div className="flex items-center gap-1 shrink-0">
                                  <Button
                                    type="button"
                                    title="Add document to section"
                                    onClick={() => {
                                      if (isCollapsed) {
                                        setCollapsedSections((prev) => ({
                                          ...prev,
                                          [sec.id]: false,
                                        }));
                                      }
                                      addSub(si);
                                    }}
                                    className="h-7 w-7 !p-0 rounded-lg flex items-center justify-center text-text-primary/70 hover:text-text-primary hover:bg-background-icon"
                                  >
                                    <FilePlus size={16} />
                                  </Button>
                                  <Button
                                    type="button"
                                    title="Remove section"
                                    onClick={() => removeSection(si)}
                                    className="h-7 w-7 !p-0 rounded-lg flex items-center justify-center text-tone-rose/80 hover:text-tone-rose hover:bg-tone-rose-soft/40"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                              )}
                            </div>

                            {/* Documents accordion content */}
                            <AccordionPrimitive.Panel className="ml-2.5 mr-1 my-2 pl-3 flex flex-col gap-1.5 border-l-2 border-button-primary/25">
                              {sec.subsections.length > 0 && (
                                <div className="max-h-[175px] overflow-y-auto custom-scrollbar pr-1 flex flex-col gap-1.5">
                                  {sec.subsections.map((sub, ssi) => {
                                    const active = sub.id === selectedSubId;
                                    const docInvalid = showErrors && (!sub.name.trim() || sub.requiredFields.length === 0);

                                    return (
                                      <div
                                        key={sub.id}
                                        className={cn(
                                          "group flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer transition-all",
                                          active
                                            ? "border-border-primary bg-background-tertiary shadow-sm"
                                            : docInvalid
                                              ? "border-tone-rose/60 bg-background-secondary"
                                              : "border-border-primary/70 bg-background-secondary hover:border-border-primary",
                                        )}
                                        onClick={() => setSelectedSubId(sub.id)}
                                      >
                                        <FileText size={13} className={cn("shrink-0", active ? "text-text-primary" : "text-text-primary/50")} />
                                        <span
                                          className={cn(
                                            "flex-1 truncate max-w-[140px] sm:max-w-[170px] text-xs font-medium",
                                            sub.name ? "text-text-primary" : "text-text-primary/40 italic",
                                          )}
                                        >
                                          {sub.name || "Unnamed Document"}
                                          {sub.isRequired !== false && (
                                            <span className="text-tone-rose font-bold text-xs ml-0.5" title="Required Document">
                                              *
                                            </span>
                                          )}
                                        </span>
                                        <span className="shrink-0 text-[11px] font-semibold text-text-primary/70 bg-background-primary/60 px-1.5 py-0.5 rounded border border-border-primary/40">
                                          {Number(sub.weightPercentage) || 0}%
                                        </span>
                                        {!readOnly && (
                                          <Button
                                            type="button"
                                            title="Remove document"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              removeSub(si, ssi);
                                            }}
                                            className="h-5 w-5 !p-0 shrink-0 rounded-md flex items-center justify-center text-tone-rose/80 hover:text-tone-rose hover:bg-tone-rose-soft/40"
                                          >
                                            <Trash2 size={12} />
                                          </Button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {sectionEmpty && (
                                <p className={cn("text-[11px] py-1", flagSection ? "text-tone-rose font-medium" : "text-text-primary/40")}>
                                  {flagSection ? "Add at least 1 document" : "No documents yet"}
                                </p>
                              )}

                              {!readOnly && (
                                <Button
                                  onClick={() => addSub(si)}
                                  className="w-full mt-1.5 py-2 px-3 rounded-2xl border border-dashed border-border-primary bg-background-form/20 hover:bg-background-form/40 text-xs font-medium text-text-primary/80 hover:text-text-primary flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                                >
                                  <Plus size={11} strokeWidth={2.5} />
                                  <span>Add Document</span>
                                </Button>
                              )}
                            </AccordionPrimitive.Panel>
                          </AccordionPrimitive.Item>
                        );
                      })}
                    </AccordionPrimitive.Root>

                    {!readOnly && (
                      <ButtonShadcn
                        variant="outline"
                        type="button"
                        onClick={addSection}
                        className="w-full border-border-primary rounded-xl text-xs py-2 text-text-secondary hover:text-text-primary hover:bg-background-tertiary flex items-center justify-center gap-1"
                      >
                        <FolderPlus size={12} /> Add Section
                      </ButtonShadcn>
                    )}
                  </>
                )}
              </div>

              {/* RIGHT — selected document */}
              <div className="w-full md:w-[65%] overflow-y-auto custom-scrollbar p-4 flex flex-col gap-4">
                {!selected ? (
                  <div className="h-full min-h-[300px] rounded-2xl border-2 border-dashed border-border-primary flex flex-col items-center justify-center gap-3 text-center p-6 bg-background-form/5">
                    <div className="h-12 w-12 rounded-2xl bg-background-tertiary flex items-center justify-center border border-border-primary/60">
                      <FileText size={22} className="text-text-primary/40" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-text-primary">No document selected</p>
                      <p className="text-xs text-text-secondary mt-0.5 max-w-[280px]">Select a document from the left tree to view configuration.</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {/* Document Details Card */}
                    <div className="rounded-xl border border-border-primary p-4 bg-background-secondary flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs font-semibold text-text-primary">
                          <SlidersHorizontal size={14} className="text-text-primary/70" />
                          <span>Document Details</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-text-secondary font-medium">Required</span>
                          <Switch
                            checked={selected.sub.isRequired ?? true}
                            disabled={readOnly}
                            onToggle={() => {
                              updateSub(selected.si, selected.ssi, {
                                isRequired: !(selected.sub.isRequired ?? true),
                              });
                            }}
                            className="scale-75 origin-right"
                            label="Required Document"
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 flex-wrap">
                        <div className="flex-1 min-w-[180px]">
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-xs font-medium text-text-primary/80">
                              Document Name
                              {selected.sub.isRequired !== false && <span className="text-tone-rose font-bold text-sm ml-1">*</span>}
                            </label>
                            <CharCount value={selected.sub.name} max={DOCUMENT_NAME_MAX_LENGTH} />
                          </div>
                          <Input
                            value={selected.sub.name}
                            disabled={readOnly}
                            onChange={(e) =>
                              updateSub(selected.si, selected.ssi, {
                                name: e.target.value,
                              })
                            }
                            placeholder="e.g. Passport"
                            maxLength={DOCUMENT_NAME_MAX_LENGTH}
                            className={cn("rounded-lg", showErrors && documentNameError && "border-tone-rose")}
                          />
                          {showErrors && documentNameError && <p className="mt-1 text-[11px] text-tone-rose">{documentNameError}</p>}
                        </div>
                        <div className="w-36">
                          <label className="block text-xs font-medium mb-1.5 text-text-primary/80">Weight (%)</label>
                          {/* Floor of 1, matching the Zod schema and the
                            server's `ge=1`: a zero-weight document can never
                            move the candidate's approval percentage. The empty
                            string is preserved rather than coerced, because an
                            input that jumps to 1 the moment it is cleared
                            cannot be typed into. The schema refuses the empty
                            value, so Save still catches it. */}
                          <Input
                            type="number"
                            min={1}
                            max={100}
                            disabled={readOnly}
                            value={selected.sub.weightPercentage}
                            onChange={(e) => {
                              const raw = e.target.value;
                              let val = raw;
                              if (raw !== "") {
                                const num = Number(raw);
                                if (num > 100) val = 100;
                                else if (num < 1) val = 1;
                                else val = num;
                              }
                              updateSub(selected.si, selected.ssi, {
                                weightPercentage: val,
                              });
                            }}
                            className="rounded-lg"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Fields to collect Card */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-text-primary">Fields to Collect ({selected.sub.requiredFields.length})</span>
                          <CustomTooltip text="Specify the key data fields to extract and verify from this document (e.g. Name, ID Number, Expiry Date).">
                            <Info size={13} className="text-text-secondary hover:text-text-primary cursor-pointer transition-colors" />
                          </CustomTooltip>
                        </div>
                        {!readOnly && (
                          <Button
                            onClick={addFieldToSelected}
                            className="p-0 rounded-none text-xs text-text-primary hover:text-text-primary/80 font-semibold flex items-center gap-1 cursor-pointer"
                          >
                            <Plus size={13} /> Add Field
                          </Button>
                        )}
                      </div>

                      <div className="rounded-xl border border-border-primary overflow-hidden bg-background-secondary">
                        {/* Table Header */}
                        <div className="flex items-center gap-3 px-3 py-2 border-b border-border-primary bg-background-tertiary/40 text-xs font-semibold text-text-secondary">
                          <div className="flex-1">Field Name</div>
                          <div className="w-[130px]">Type</div>
                          {!readOnly && <div className="w-10 text-center">Delete</div>}
                        </div>

                        {/* Table Rows.

                            No scroll cap of its own. This table sits inside the
                            detail panel, which already scrolls, and a
                            `max-h` here put a second scrollbar beside that one
                            — two tracks down the right of the same column, each
                            moving a different amount of the same list. The
                            panel owns the scrolling; the table just grows. */}
                        {selected.sub.requiredFields.length === 0 ? (
                          <div className="p-4 text-center">
                            <p className={cn("text-xs", showErrors && selected.sub.requiredFields.length === 0 ? "text-tone-rose font-medium" : "text-text-secondary")}>
                              {showErrors && selected.sub.requiredFields.length === 0
                                ? "This document needs at least 1 required field."
                                : 'No fields configured yet. Click "+ Add field" above.'}
                            </p>
                          </div>
                        ) : (
                          <div className="divide-y divide-border-primary/60">
                            {selected.sub.requiredFields.map((f, fi) => (
                              <div key={fi} className="flex items-center gap-3 px-3 py-2 bg-background-secondary">
                                <div className="flex-1">
                                  <Input
                                    value={f.fieldName}
                                    disabled={readOnly}
                                    onChange={(e) =>
                                      updateFieldOfSelected(fi, {
                                        fieldName: e.target.value,
                                      })
                                    }
                                    placeholder="e.g. Name"
                                    maxLength={FIELD_NAME_MAX_LENGTH}
                                    className={cn("rounded-lg py-1.5 text-xs", showErrors && fieldNameErrorAt(fi) && "border-tone-rose")}
                                  />
                                  <div className="mt-1 flex items-start justify-between gap-2">
                                    <p className="text-[11px] text-tone-rose">{(showErrors && fieldNameErrorAt(fi)) || ""}</p>
                                    <CharCount value={f.fieldName} max={FIELD_NAME_MAX_LENGTH} className="shrink-0" />
                                  </div>
                                </div>
                                {/* Aligned to the top of the row rather than
                                    centred: the field-name cell beside it grows
                                    a second line for its error and character
                                    count, and a centred Select drifts down the
                                    row as that line appears and disappears. */}
                                <div className="w-[130px] shrink-0 self-start">
                                  <Select
                                    value={f.fieldType}
                                    disabled={readOnly}
                                    onChange={(fieldType) =>
                                      updateFieldOfSelected(fi, {
                                        fieldType,
                                      })
                                    }
                                    options={REQUIRED_FIELD_TYPES.map((type) => ({
                                      value: type,
                                      label: type,
                                    }))}
                                    // Height left to the kit. `h-auto
                                    // min-h-[34px]` replaced Button's fixed
                                    // `h-8` with a floating one, so this
                                    // control sat a couple of pixels off every
                                    // Input it shares a row with.
                                    className="w-full rounded-lg border-border-input bg-background-form/40 px-2 text-xs text-text-primary"
                                  />
                                </div>
                                {!readOnly && (
                                  <div className="w-10 shrink-0 flex items-center justify-center">
                                    <Button
                                      type="button"
                                      title="Remove field"
                                      onClick={() => removeFieldFromSelected(fi)}
                                      className="h-7 w-7 !p-0 rounded-md flex items-center justify-center text-tone-rose/80 hover:text-tone-rose hover:bg-tone-rose-soft/40"
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="flex flex-col gap-2 px-5 py-3 border-t border-border-primary shrink-0">
          {!readOnly && <ModuleUnavailableNotice module={MODULES.COMPLIANCE} suffix="This checklist can be read but not saved." />}

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 text-xs font-medium">
              {weightsValid ? (
                <span className="flex items-center gap-1.5 text-tone-emerald">
                  <CheckCircle2 size={15} />
                  {/* A country set is legal below 100, so
                                        reporting "equals 100%" on a valid 70%
                                        would state something untrue. It reports
                                        the total instead, and that the total is
                                        what will be saved. */}
                  {editingMaster ? `Total weight is ${weightTotal}% — saved as the starting point` : "Total weight equals 100%"}
                </span>
              ) : prefilledFrom ? (
                // Prefilled from a country document set, so the
                // total is deliberately short of 100 — that
                // headroom is the point of the feature. Saying
                // only "must equal 100%" here reads as a fault
                // in the template rather than the room the
                // officer is meant to spend.
                <span className="flex items-center gap-1.5 text-text-secondary">
                  <AlertCircle size={15} className="text-tone-amber" />
                  <span>{weightTotal}% of 100% assigned — add your own documents, then set the weights to total 100%.</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-tone-rose">
                  <AlertCircle size={15} /> Total weight is {weightTotal}%{editingMaster ? " (must be between 1% and 100%)" : " (must equal 100%)"}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <DialogClose
                render={
                  <ButtonShadcn variant="outline" className="border-none rounded-lg text-xs" type="button" disabled={submitting}>
                    {readOnly ? "Close" : "Cancel"}
                  </ButtonShadcn>
                }
              />
              {!readOnly && (
                // Hovering the button explains what is still missing if invalid
                <CustomTooltip text={missingFieldsTooltip}>
                  <ButtonShadcn
                    className="bg-button-secondary text-text-base rounded-lg text-xs"
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || !isAvailable}
                  >
                    {submitting ? "Saving…" : isEdit ? "Save Changes" : "Create Checklist"}
                  </ButtonShadcn>
                </CustomTooltip>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChecklistBuilderModal;
